import React from "react";
import { connect } from "react-redux";
import {
  showDialog,
  hideDialog,
  repositionDesign,
  selectSingleElement
} from "../../actions";
import { bindActionCreators } from "redux";
import CanvasContainer from "./CanvasContainer";
import TopControls from "./Controls/TopControls";
import LeftControls from "./Controls/LeftControls";
import RightControls from "./Controls/RightControls";
import MobileControls from "./Controls/MobileControls";
import Dimensions from "./Dimensions";
import ItemTypes from "../../constants/ItemTypes";
import { DropTarget } from "react-dnd";
import { dropElement, dropSelectedElements } from "../../actions";
import { calculateBoundingBoxForSelectedElements } from "../common/layer";
import "./Canvas.css";
import localStorageWrapper from "../../services/localStorageWrapper";

const boxTarget = {
  drop(props, monitor, component) {
    const item = monitor.getItem();
    const delta = monitor.getDifferenceFromInitialOffset();
    const layer = props.design.layer_set[item.layerId];
    const layerWidth = layer.width * 72 * props.design.preview_scale;
    const layerHeight = layer.height * 72 * props.design.preview_scale;

    let left = null,
      top = null;

    let snapX = 0,
      snapY = 0;

    if (item.isSelected) {
      const boundingbox = calculateBoundingBoxForSelectedElements(
        props.design,
        props.design.layer_set[item.layerId]
      );
      left = Math.round(boundingbox.minX + delta.x);
      top = Math.round(boundingbox.minY + delta.y);

      const boundingBoxCenterX = boundingbox.width / 2,
        boundingBoxCenterXPos = boundingBoxCenterX + left,
        boundingBoxCenterY = boundingbox.height / 2,
        boundingBoxCenterYPos = boundingBoxCenterY + top;

      const centerXDifference = layerWidth / 2 - boundingBoxCenterXPos,
        centerYDifference = layerHeight / 2 - boundingBoxCenterYPos;

      if (
        centerXDifference <= 15 &&
        centerXDifference >= -15 &&
        item.guidesVisible
      ) {
        snapX = centerXDifference;
      } else {
        snapX = 0;
      }

      if (
        centerYDifference <= 15 &&
        centerYDifference >= -15 &&
        item.guidesVisible
      ) {
        snapY = centerYDifference;
      } else {
        snapY = 0;
      }

      if (left < 0) {
        delta.x += 0 - left;
      }
      if (top < 0) {
        delta.y += 0 - top;
      }

      if (left + boundingbox.width > layerWidth) {
        const newLeft = layerWidth - boundingbox.width;
        delta.x += newLeft - left;
      }
      if (top + boundingbox.height > layerHeight) {
        const newTop = layerHeight - boundingbox.height;
        delta.y += newTop - top;
      }
    } else {
      left = Math.round(item.left + delta.x);
      top = Math.round(item.top + delta.y);
      left = left < 0 ? 0 : left;
      top = top < 0 ? 0 : top;
      if (left + item.width >= layerWidth) {
        left = layerWidth - item.width;
      }
      if (top + item.height >= layerHeight) {
        top = layerHeight - item.height;
      }
    }

    const payload = {
      ...item,
      original_left: item.left,
      original_top: item.top,
      left,
      top,
      delta,
      snapX: snapX,
      snapY: snapY,
      guidesVisible: item.guidesVisible
    };

    if (item.isSelected) {
      props.dropSelectedElements(payload);
    } else {
      props.dropElement(payload);
    }
  }
};

class Canvas extends React.Component {
  state = {
    guidesVisible: true,
    didShowInitialTips: false,
    didShow2ndTip: false,
    didShowHoloMessage: false
  };

  onToggleGuides = () => {
    // Toggle Guides visibility
    this.setState((prevState) => {
      return { guidesVisible: !prevState.guidesVisible };
    });
  };

  onShowDialog = (args) => {
    this.props.showDialog({
      title: args.title,
      message: args.message,
      topPosition: args.topPosition,
      leftPosition: args.leftPosition,
      origin: args.origin
    });
  };

  onHideDialog = () => {
    // Dismiss the dialog box
    this.props.hideDialog();
  };

  handleShowHoloMessage = () => {
    this.showHoloMessage();
    this.setState({ didShowHoloMessage: true });
  };

  showHoloMessage = () => {
    this.onShowDialog({
      title: "Designer's Note",
      message:
        "Our shiny holographic foils come to life in the light! When you hold the product up and move it side to side, you will see the holographic effect blossom and change color like magic!",
      topPosition: 50,
      leftPosition: 340,
      origin: "bottomleft"
    });
  };

  handleShowInitialTips = () => {
    this.showInitialTips();
    this.setState({ didShowInitialTips: true });
  };

  showInitialTips = () => {
    // Check if we're on a mobile device
    const isMobile = window.innerWidth <= 768;

    this.onShowDialog({
      title: "Designer's Tip",
      message: "Click on the text or graphic to edit and move on product.",
      // For mobile, position doesn't matter as it will be centered by CSS
      // For desktop, use the original position
      topPosition: isMobile ? 0 : 0,
      leftPosition: isMobile ? 0 : 0,
      origin: isMobile ? "bottomleft" : "bottomright"
    });
  };

  onShow2ndTip = () => {
    this.onHideDialog();
    this.handleShow2ndTip();
    this.setState({ didShow2ndTip: true });
  };

  handleShow2ndTip = () => {
    // Check if we're on a mobile device
    const isMobile = window.innerWidth <= 768;

    // Don't show the tip on mobile devices since they don't have a keyboard
    if (isMobile) {
      return;
    }

    // Only show the tip on desktop
    this.onShowDialog({
      title: "Designer's Tip",
      message: "Hold the Shift key and click to select multiple elements.",
      topPosition: 50,
      leftPosition: 340,
      origin: "bottomleft"
    });
  };

  handleHide2ndTip = () => {
    this.onHideDialog();
  };

  componentDidMount() {
    // Expiration for local storage
    const now = Date.now(),
      expires = 60 * 60 * 1000 * 24;

    if (
      !this.state.didShowInitialTips &&
      !localStorageWrapper.getItem("fypTipsShown")
    ) {
      this.handleShowInitialTips();
      // Set local storage for 24 hours to remember that the tips have been shown
      localStorageWrapper.setItem("fypTipsShown", true);
      localStorageWrapper.setItem("fypTipsShownTimestamp", now);
    }

    // Delete local storage after 24 hours
    if (now - localStorageWrapper.getItem("fypTipsShownTimestamp") > expires) {
      localStorageWrapper.removeItem("fypTipsShown");
      localStorageWrapper.removeItem("fypTipsShownTimestamp");
    }
  }

  checkIfHoloFound() {
    const holographic_print_ids = [4315, 4314, 4316]; // IDs of Shiny Holographic Silver and Gold print colors

    for (let d in this.props.design.layer_set) {
      if (
        this.props.design.layer_set[d].has_print_color !== null && // Check to make sure colors are loaded too avoid any recursive errors on null stuff.
        this.props.design.layer_set[d].has_print_color !== false
      ) {
        const printColorData = this.props.design.layer_set[d];
        const { print_color = {} } = printColorData;

        holographic_print_ids.map((id) => {
          if (print_color !== null) {
            if (print_color.id == id && print_color.is_active == true) {
              localStorageWrapper.setItem("wasShinyHoloSelected", true);
              this.handleShowHoloMessage();
            }
          }
        });
      }
    }
  }

  componentDidUpdate(prevProps) {
    // Check if selected product option has changed, and reposition elements if they're outside the printable area
    if (
      prevProps.design.product_option_id !==
        this.props.design.product_option_id &&
      this.props.allElementsLoaded
    ) {
      this.props.repositionDesign();
    }

    if (!this.state.didShowHoloMessage) {
      this.checkIfHoloFound();
    }
  }

  render() {
    return this.props.connectDropTarget(
      <div className="canvas">
        {this.props.design.id && (
          <TopControls
            toggleTools={this.props.toggleTools}
            toolsVisible={this.props.toolsVisible}
            designName={this.props.design.name}
          />
        )}

        <CanvasContainer
          guidesVisible={this.state.guidesVisible}
          handleHideInitialTips={this.handleHideInitialTips}
          onShow2ndTip={this.onShow2ndTip}
          didShow2ndTip={this.state.didShow2ndTip}
          handleHide2ndTip={this.handleHide2ndTip}
          dialog={this.props.dialog}
          onHideDialog={this.onHideDialog}
          compactStyle={this.props.compactStyle}
        />

        <LeftControls />
        <RightControls
          onToggleGuides={this.onToggleGuides}
          guidesVisible={this.state.guidesVisible}
          handleZoom={this.props.handleZoom}
        />

        <MobileControls
          onToggleGuides={this.onToggleGuides}
          guidesVisible={this.state.guidesVisible}
        />

        <Dimensions />
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    design: state.design.present,
    dialog: state.dialog,
    initialTips: state.initialTips,
    allElementsLoaded: state.verifyElementsLoaded.allElementsLoaded,
    invalidDialog: state.invalidDialog,
    mobileDrawer: state.mobileDrawer
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      showDialog,
      hideDialog,
      dropElement,
      dropSelectedElements,
      repositionDesign,
      selectSingleElement
    },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  DropTarget([ItemTypes.TEXT, ItemTypes.IMAGE], boxTarget, (connect) => ({
    connectDropTarget: connect.dropTarget()
  }))(Canvas)
);
